import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import Button, { ButtonVariant } from 'src/components/atoms/Button';
import { XrayTab, XrayTabGroup } from 'src/ts/enums/xray';
import { IXrayAnalysis, IXraySpanPage, IXrayTable } from 'src/ts/interfaces/xray';
import {
  XrayCenteredState,
  XrayFontDescriptorName,
  XrayFontDescriptorsScroller,
  XrayPanelContainer,
  XraySectionHeading,
  XrayRawText,
  XrayTabButton,
  XrayTabContent,
  XrayTabStrip
} from '../../styles';
import { XrayStatus } from '../../hooks/useXrayAnalysis';

export const XRAY_TAB_LABELS: Record<XrayTab, string> = {
  [XrayTab.FileMetadata]: 'File Metadata',
  [XrayTab.Metadata]: 'Metadata',
  [XrayTab.Font]: 'Font',
  [XrayTab.Size]: 'Size',
  [XrayTab.TextBreakups]: 'Text Breakups',
  [XrayTab.ActiveFields]: 'Active Fields',
  [XrayTab.FontDescriptors]: 'Font Descriptors',
  [XrayTab.ByteCode]: 'Byte Code',
  [XrayTab.SimilarDocuments]: 'Similar Documents'
};

export const XRAY_TABS = Object.values(XrayTab);

export const XRAY_TAB_GROUPS: Record<XrayTabGroup, { label: string; sections: XrayTab[] }> = {
  [XrayTabGroup.Metadata]: {
    label: 'Metadata',
    sections: [XrayTab.FileMetadata, XrayTab.Metadata, XrayTab.Font, XrayTab.Size]
  },
  [XrayTabGroup.TextProperties]: {
    label: 'Text Properties',
    sections: [XrayTab.ActiveFields, XrayTab.TextBreakups]
  },
  [XrayTabGroup.FontDescriptors]: {
    label: 'Font Descriptors',
    sections: [XrayTab.FontDescriptors]
  },
  [XrayTabGroup.ByteCode]: { label: 'Byte Code', sections: [XrayTab.ByteCode] },
  [XrayTabGroup.SimilarDocuments]: {
    label: 'Similar Documents',
    sections: [XrayTab.SimilarDocuments]
  }
};

export const XRAY_GROUP_ORDER = Object.values(XrayTabGroup);

interface XrayPanelProps {
  status: XrayStatus;
  data: IXrayAnalysis | null;
  error: string | null;
  onRetry: () => void;
}

const NO_METADATA = 'No metadata found';
const NO_FONT_DESCRIPTORS = 'No font descriptors found';
const FONT_DESCRIPTORS_UNAVAILABLE = 'Font descriptors not available yet';
export const BYTE_CODE_MAX_CHARS = 200_000;

const tabId = (group: XrayTabGroup) => `xray-tab-${group}`;
const descriptorName = (keys: [string, string][]): string =>
  (keys.find(([key]) => key === 'FontName')?.[1] ?? '')
    .replace(/^\//, '')
    .replace(/^[A-Z]{6}\+/, '');
const rowCount = (table: IXrayTable | null | undefined) => table?.rows?.length ?? 0;

const DataTable: FC<{ table: IXrayTable | null | undefined; label: string }> = ({
  table,
  label
}) => {
  if (!table) return <p>{NO_METADATA}</p>;
  return (
    <table aria-label={label}>
      <thead>
        <tr>
          {(table.columns ?? []).map((column) => (
            <th key={column}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(table.rows ?? []).map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIndex}>
            {row.map((value, cellIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <td key={cellIndex} className={String(value ?? '').length > 24 ? 'long' : undefined}>
                {value === null ? '' : String(value)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SpanPages: FC<{ pages: IXraySpanPage[] }> = ({ pages }) => (
  <>
    {pages.map((page) => (
      <table key={page.page} aria-label={`Page ${page.page} spans`}>
        <thead>
          <tr>
            <th colSpan={3}>Page {page.page}</th>
          </tr>
          <tr>
            <th>Font</th>
            <th>Color</th>
            <th>Text</th>
          </tr>
        </thead>
        <tbody>
          {(page.items ?? []).map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              <td>{item.font}</td>
              <td>{item.color}</td>
              <XrayRawText>{item.text}</XrayRawText>
            </tr>
          ))}
        </tbody>
      </table>
    ))}
  </>
);

const SectionContent: FC<{ tab: XrayTab; data: IXrayAnalysis }> = ({ tab, data }) => {
  switch (tab) {
    case XrayTab.FileMetadata:
      return <DataTable table={data.file_metadata} label="File metadata" />;
    case XrayTab.Metadata:
      return <pre>{data.metadata ?? NO_METADATA}</pre>;
    case XrayTab.Font:
      return <pre>{data.font ?? NO_METADATA}</pre>;
    case XrayTab.Size: {
      const pages = data.size ?? [];
      if (pages.length === 0) return <p>{NO_METADATA}</p>;
      return (
        <div>
          {pages.map((page) => (
            <div key={page.page}>
              width {page.width_inch} - height {page.height_inch}
            </div>
          ))}
        </div>
      );
    }
    case XrayTab.TextBreakups: {
      const spans = data.text_breakups?.spans ?? [];
      const secondPass = data.text_breakups?.second_pass ?? [];
      if (spans.length === 0 && secondPass.length === 0) return <p>{NO_METADATA}</p>;
      return (
        <>
          <SpanPages pages={spans} />
          {secondPass.map((page) => (
            <table key={`second-${page.page}`} aria-label={`Page ${page.page} second pass`}>
              <thead>
                <tr>
                  <th colSpan={2}>Page {page.page}</th>
                </tr>
              </thead>
              <tbody>
                {(page.items ?? []).map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td>{item.font_name}</td>
                    <XrayRawText>{item.text}</XrayRawText>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </>
      );
    }
    case XrayTab.ActiveFields: {
      const entries = Object.entries(data.active_fields ?? {});
      if (entries.length === 0) return <p>No active fields</p>;
      return (
        <table aria-label="Active fields">
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td>{key}</td>
                <XrayRawText>{value ?? ''}</XrayRawText>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case XrayTab.FontDescriptors: {
      const descriptors = data.font_descriptors;
      return (
        <>
          {descriptors == null ? (
            <p>{FONT_DESCRIPTORS_UNAVAILABLE}</p>
          ) : descriptors.length === 0 ? (
            <p>{NO_FONT_DESCRIPTORS}</p>
          ) : (
            <XrayFontDescriptorsScroller role="region" aria-label="Font descriptors" tabIndex={0}>
              <table>
                <tbody>
                  {descriptors.map((descriptor) => {
                    const name = descriptorName(descriptor.keys);
                    return (
                      <tr key={descriptor.object}>
                        <XrayFontDescriptorName scope="row">
                          <div>Object {descriptor.object}</div>
                          {name && <div>{name}</div>}
                        </XrayFontDescriptorName>
                        {descriptor.keys.flatMap(([key, value], index) => [
                          <td key={`key-${index}`}>
                            <div>{key}</div>
                          </td>,
                          <td key={`value-${index}`}>
                            <div>{value}</div>
                          </td>
                        ])}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </XrayFontDescriptorsScroller>
          )}
          {data.font_descriptors_note && <p>{data.font_descriptors_note}</p>}
        </>
      );
    }
    case XrayTab.ByteCode: {
      const byteCode = data.byte_code ?? '';
      if (byteCode.length === 0) return <p>{NO_METADATA}</p>;
      if (byteCode.length <= BYTE_CODE_MAX_CHARS) return <pre>{byteCode}</pre>;
      return (
        <>
          <p>
            Showing the first {BYTE_CODE_MAX_CHARS.toLocaleString()} of{' '}
            {byteCode.length.toLocaleString()} characters
          </p>
          <pre>{byteCode.slice(0, BYTE_CODE_MAX_CHARS)}</pre>
        </>
      );
    }
    case XrayTab.SimilarDocuments: {
      const similar = data.similar_documents;
      if (!similar) return <p>{NO_METADATA}</p>;
      if (similar.note) return <p>{similar.note}</p>;
      return (
        <>
          <p>PRODUCER: {similar.producer}</p>
          <XraySectionHeading>Exact ({rowCount(similar.exact)})</XraySectionHeading>
          <DataTable table={similar.exact} label="Exact matches" />
          <XraySectionHeading>Edit ({rowCount(similar.edited)})</XraySectionHeading>
          <DataTable table={similar.edited} label="Edited matches" />
          <XraySectionHeading>Clean ({rowCount(similar.clean)})</XraySectionHeading>
          <DataTable table={similar.clean} label="Clean matches" />
          <XraySectionHeading>Undetermined ({rowCount(similar.undetermined)})</XraySectionHeading>
          <DataTable table={similar.undetermined} label="Undetermined matches" />
        </>
      );
    }
    default:
      return null;
  }
};

const XrayPanel: FC<XrayPanelProps> = ({ status, data, error, onRetry }) => {
  const [activeTab, setActiveTab] = useState<XrayTabGroup>(XrayTabGroup.Metadata);
  const tabRefs = useRef<Partial<Record<XrayTabGroup, HTMLButtonElement | null>>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeTab]);

  const focusTab = (tab: XrayTabGroup) => {
    setActiveTab(tab);
    tabRefs.current[tab]?.focus();
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = XRAY_GROUP_ORDER.indexOf(activeTab);
    const last = XRAY_GROUP_ORDER.length - 1;
    switch (event.key) {
      case 'ArrowRight':
        focusTab(XRAY_GROUP_ORDER[index === last ? 0 : index + 1]);
        break;
      case 'ArrowLeft':
        focusTab(XRAY_GROUP_ORDER[index === 0 ? last : index - 1]);
        break;
      case 'Home':
        focusTab(XRAY_GROUP_ORDER[0]);
        break;
      case 'End':
        focusTab(XRAY_GROUP_ORDER[last]);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  if (status === 'loading' || status === 'idle') {
    return (
      <XrayCenteredState data-testid="xray-loading">
        <span>Analyzing document…</span>
      </XrayCenteredState>
    );
  }

  if (status === 'error' || !data) {
    return (
      <XrayCenteredState data-testid="xray-error">
        <span>{error ?? 'X-Ray analysis failed'}</span>
        <Button name="xray-retry" label="Retry" variant={ButtonVariant.outline} onClick={onRetry}>
          Retry
        </Button>
      </XrayCenteredState>
    );
  }

  const { sections } = XRAY_TAB_GROUPS[activeTab];

  return (
    <XrayPanelContainer data-testid="xray-panel">
      <XrayTabStrip role="tablist" aria-label="X-Ray sections" onKeyDown={onTabKeyDown}>
        {XRAY_GROUP_ORDER.map((tab) => (
          <XrayTabButton
            key={tab}
            ref={(element) => {
              tabRefs.current[tab] = element;
            }}
            id={tabId(tab)}
            data-testid={tabId(tab)}
            type="button"
            role="tab"
            aria-selected={tab === activeTab}
            aria-controls="xray-tabpanel"
            tabIndex={tab === activeTab ? 0 : -1}
            $active={tab === activeTab}
            onClick={() => setActiveTab(tab)}
          >
            {XRAY_TAB_GROUPS[tab].label}
          </XrayTabButton>
        ))}
      </XrayTabStrip>
      <XrayTabContent
        ref={contentRef}
        id="xray-tabpanel"
        role="tabpanel"
        aria-labelledby={tabId(activeTab)}
      >
        {data.warning && <p>WARNING: {data.warning}</p>}
        {data.error && <p>ERROR: {data.error}</p>}
        {sections.map((section) => (
          <div key={section}>
            {sections.length > 1 && (
              <XraySectionHeading>{XRAY_TAB_LABELS[section]}</XraySectionHeading>
            )}
            <SectionContent tab={section} data={data} />
          </div>
        ))}
      </XrayTabContent>
    </XrayPanelContainer>
  );
};

export default XrayPanel;
